import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { FamilyMessage, MessageThreadDetail } from '../../../../lib/api';
import { formatDateToJapanese } from '../../../../utils/dateFormat';
import { LockIcon, CheckCircleIcon, ArrowIcon } from '../../../../components/Icons/Icons';

interface MessageListProps {
  threads: MessageThreadDetail[];
  patientId: number | null;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

// --- 共通カードベース ---
const CardBase = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

// --- 非表示設定カード ---
const HiddenCard = styled(CardBase)`
  border: 2px solid ${({ theme }) => theme.colors.border.supplementary};
`;

const HiddenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.backgroundSupplementary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const CommentNavSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 16px 4px;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;

const CommentDate = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CommentNavLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChevronRight = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;

const ReplyContentBox = styled.div`
  margin: 8px 16px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.backgroundTableHeader};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ReplyDivider = styled.div`
  margin: 8px 16px;
  border-top: 1px dashed ${({ theme }) => theme.colors.border.driver};
`;

const MessageBodySection = styled.div`
  padding: 12px 16px 14px;
`;

// 送信者名（左）とタイムスタンプ（右）を同じ行に並べる
// 文字サイズが異なる（16px / 14px）ため、中央揃えでは行ボックスの中心が揃うだけで
// ベースラインが 1.5px ずれる。文字の揃いはベースラインで見えるため baseline で揃える
const MessageHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: 4px;
`;

const SenderName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  /* 長い送信者名でタイムスタンプを押し出さないよう、名前側を縮退させて省略表示する */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MessageContent = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Timestamp = styled.span`
  flex-shrink: 0;
  /* 送信者名が無い場合も右上に配置する（space-between だけでは左寄せになる） */
  margin-left: auto;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.backgroundTableGood};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.5;
`;


// --- 本人表示カード ---
const VisibleCard = styled(CardBase)``;

// --- ヘルパー関数 ---

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${formatDateToJapanese(date)} ${hours}:${minutes}`;
};

const formatSourceDateLabel = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDateToJapanese(date);
};

const getStatusDisplay = (message: FamilyMessage) => {
  return !!message.confirmed_at;
};

// --- 共通サブコンポーネント ---

const ReplyQuoteSection: React.FC<{
  replyTo: FamilyMessage['reply_to'];
  onCommentClick: (sourceDate: string) => void;
}> = ({ replyTo, onCommentClick }) => {
  if (!replyTo) return null;

  return (
    <>
      <CommentNavSection onClick={() => onCommentClick(replyTo.source_date)}>
        <CommentDate>{formatSourceDateLabel(replyTo.source_date)}</CommentDate>
        <CommentNavLabel>連絡帳のコメント</CommentNavLabel>
        <ChevronRight><ArrowIcon /></ChevronRight>
      </CommentNavSection>
      <ReplyContentBox>{replyTo.source_content}</ReplyContentBox>
      <ReplyDivider />
    </>
  );
};

const MessageBody: React.FC<{
  message: FamilyMessage;
}> = ({ message }) => (
  <MessageBodySection>
    <MessageHeader>
      {message.sender_name && <SenderName>{message.sender_name}</SenderName>}
      <Timestamp>{formatTimestamp(message.created_at)}</Timestamp>
    </MessageHeader>
    <MessageContent>{message.content}</MessageContent>
    {getStatusDisplay(message) && (
      <MessageFooter>
        <StatusBadge>
          <CheckCircleIcon />
          スタッフ確認済
        </StatusBadge>
      </MessageFooter>
    )}
  </MessageBodySection>
);

// --- カードコンポーネント ---

const HiddenMessageCard: React.FC<{
  thread: MessageThreadDetail;
  onCommentClick: (sourceDate: string) => void;
}> = ({ thread, onCommentClick }) => (
  <HiddenCard>
    <HiddenHeader>
      <LockIcon />
      <span>非表示設定（利用者本人には見えません）</span>
    </HiddenHeader>
    {thread.messages.map((message) => (
      <React.Fragment key={message.message_id}>
        <ReplyQuoteSection replyTo={message.reply_to} onCommentClick={onCommentClick} />
        <MessageBody message={message} />
      </React.Fragment>
    ))}
  </HiddenCard>
);

const VisibleMessageCard: React.FC<{
  thread: MessageThreadDetail;
  onCommentClick: (sourceDate: string) => void;
}> = ({ thread, onCommentClick }) => (
  <VisibleCard>
    {thread.messages.map((message) => (
      <React.Fragment key={message.message_id}>
        <ReplyQuoteSection replyTo={message.reply_to} onCommentClick={onCommentClick} />
        <MessageBody message={message} />
      </React.Fragment>
    ))}
  </VisibleCard>
);

export const MessageList: React.FC<MessageListProps> = ({ threads, patientId }) => {
  const navigate = useNavigate();

  const getLatestMessageAt = (thread: MessageThreadDetail): number => {
    if (thread.messages.length === 0) return 0;
    return Math.max(...thread.messages.map(m => new Date(m.created_at).getTime()));
  };

  const sortedThreads = useMemo(
    () => [...threads].sort((a, b) => getLatestMessageAt(b) - getLatestMessageAt(a)),
    [threads]
  );

  const handleReplyQuoteClick = useCallback(
    (sourceDate: string) => {
      const params = new URLSearchParams();
      params.set('date', sourceDate);
      if (patientId) {
        params.set('patient_id', String(patientId));
      }
      navigate(`/?${params.toString()}`);
    },
    [navigate, patientId]
  );

  return (
    <Container>
      {sortedThreads.map((thread) =>
        thread.hide_from_patient ? (
          <HiddenMessageCard
            key={thread.thread_id}
            thread={thread}
            onCommentClick={handleReplyQuoteClick}
          />
        ) : (
          <VisibleMessageCard
            key={thread.thread_id}
            thread={thread}
            onCommentClick={handleReplyQuoteClick}
          />
        )
      )}
    </Container>
  );
};

export default MessageList;
